import { BenchmarkTraceEvent } from "../../types";

export enum TraceSpanKind {
  NODE = "node",
  LLM = "llm",
  ROUTE = "route",
}

export enum TraceSpanStatus {
  RUNNING = "running",
  DONE = "done",
  ERROR = "error",
}

export enum TraceTurnStatus {
  RUNNING = "running",
  DONE = "done",
  ERROR = "error",
}

export interface TraceSpan {
  key: string;
  kind: TraceSpanKind;
  status: TraceSpanStatus;
  name: string;
  startedAt: string;
  decision?: string;
  durationMs?: number;
  provider?: string;
  model?: string;
  openRouterRoutingMode?: "latency" | "throughput";
  openRouterProvider?: string;
  openRouterAttempt?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  costUsd?: number;
  estimatedCostUsd?: number;
  costSource?: "provider" | "estimated";
  error?: string;
  stateChanges?: Record<string, string | number | boolean | null>;
  isStateSnapshot?: boolean;
}

export interface TraceTurn {
  turnId: string;
  index: number;
  status: TraceTurnStatus;
  startedAt?: string;
  durationMs?: number;
  error?: string;
  spans: TraceSpan[];
}

interface TurnBuilder {
  turn: TraceTurn;
  openNodesByName: Map<string, TraceSpan[]>;
  openLlmBySpanId: Map<string, TraceSpan>;
  openLlmByName: Map<string, TraceSpan[]>;
}

export function sortTraceEvents(
  events: BenchmarkTraceEvent[],
): BenchmarkTraceEvent[] {
  return [...events].sort((left, right) => {
    if (left.turnId === right.turnId) {
      return left.sequence - right.sequence;
    }
    return Date.parse(left.timestamp) - Date.parse(right.timestamp);
  });
}

export function getTraceSpanDurationMs(
  span: TraceSpan,
  now: number,
): number | undefined {
  if (span.status !== TraceSpanStatus.RUNNING) {
    return span.durationMs;
  }
  const startedAt = Date.parse(span.startedAt);
  return Number.isNaN(startedAt) ? undefined : Math.max(now - startedAt, 0);
}

function getLlmSpanName(event: BenchmarkTraceEvent): string {
  return event.operationName ?? event.promptName ?? "LLM inference";
}

function diffMs(
  startedAt: string | undefined,
  endedAt: string,
): number | undefined {
  if (!startedAt) return undefined;
  const start = Date.parse(startedAt);
  const end = Date.parse(endedAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined;
  return Math.max(end - start, 0);
}

function applySpanDetails(span: TraceSpan, event: BenchmarkTraceEvent): void {
  span.provider = event.provider ?? span.provider;
  span.model = event.model ?? span.model;
  span.openRouterRoutingMode =
    event.openRouterRoutingMode ?? span.openRouterRoutingMode;
  span.openRouterProvider = event.openRouterProvider ?? span.openRouterProvider;
  span.openRouterAttempt = event.openRouterAttempt ?? span.openRouterAttempt;
  span.inputTokens = event.inputTokens ?? span.inputTokens;
  span.outputTokens = event.outputTokens ?? span.outputTokens;
  span.totalTokens = event.totalTokens ?? span.totalTokens;
  span.costUsd = event.costUsd ?? span.costUsd;
  span.estimatedCostUsd = event.estimatedCostUsd ?? span.estimatedCostUsd;
  span.costSource = event.costSource ?? span.costSource;
  span.error = event.error ?? span.error;

  const isSnapshot = event.type === "node.started";
  const hasState =
    event.stateChanges && Object.keys(event.stateChanges).length > 0;
  if (hasState && !(isSnapshot && span.stateChanges !== undefined)) {
    span.stateChanges = event.stateChanges;
    span.isStateSnapshot = isSnapshot;
  }
}

function createSpan(
  kind: TraceSpanKind,
  status: TraceSpanStatus,
  event: BenchmarkTraceEvent,
  name: string,
): TraceSpan {
  const span: TraceSpan = {
    key: `${event.turnId}:${event.sequence}`,
    kind,
    status,
    name,
    startedAt: event.timestamp,
  };
  applySpanDetails(span, event);
  return span;
}

function getTerminalSpanStatus(event: BenchmarkTraceEvent): TraceSpanStatus {
  return event.type.endsWith(".error")
    ? TraceSpanStatus.ERROR
    : TraceSpanStatus.DONE;
}

function closeSpan(span: TraceSpan, event: BenchmarkTraceEvent): void {
  span.status = getTerminalSpanStatus(event);
  span.durationMs = event.durationMs ?? diffMs(span.startedAt, event.timestamp);
  applySpanDetails(span, event);
}

function createClosedSpan(
  kind: TraceSpanKind,
  event: BenchmarkTraceEvent,
  name: string,
): TraceSpan {
  const span = createSpan(kind, getTerminalSpanStatus(event), event, name);
  span.durationMs = event.durationMs;
  return span;
}

function pushOpen(
  stacks: Map<string, TraceSpan[]>,
  name: string,
  span: TraceSpan,
): void {
  const stack = stacks.get(name) ?? [];
  stack.push(span);
  stacks.set(name, stack);
}

function popOpen(
  stacks: Map<string, TraceSpan[]>,
  name: string,
): TraceSpan | undefined {
  const stack = stacks.get(name);
  while (stack && stack.length > 0) {
    const span = stack.pop();
    if (span?.status === TraceSpanStatus.RUNNING) {
      return span;
    }
  }
  return undefined;
}

/**
 * Folds the raw start/complete/error event stream into one span per unit of
 * work, grouped by turn, so the sidebar can render a readable timeline.
 */
export function buildTraceTurns(
  events: BenchmarkTraceEvent[] = [],
): TraceTurn[] {
  const builders = new Map<string, TurnBuilder>();
  const ordered: TurnBuilder[] = [];

  const getBuilder = (turnId: string): TurnBuilder => {
    const existing = builders.get(turnId);
    if (existing) return existing;
    const builder: TurnBuilder = {
      turn: {
        turnId,
        index: ordered.length + 1,
        status: TraceTurnStatus.RUNNING,
        spans: [],
      },
      openNodesByName: new Map(),
      openLlmBySpanId: new Map(),
      openLlmByName: new Map(),
    };
    builders.set(turnId, builder);
    ordered.push(builder);
    return builder;
  };

  for (const event of sortTraceEvents(events)) {
    const builder = getBuilder(event.turnId);
    const { turn } = builder;

    switch (event.type) {
      case "turn.started": {
        turn.startedAt = event.timestamp;
        turn.status = TraceTurnStatus.RUNNING;
        break;
      }
      case "turn.completed": {
        turn.status = TraceTurnStatus.DONE;
        turn.durationMs =
          event.durationMs ?? diffMs(turn.startedAt, event.timestamp);
        break;
      }
      case "turn.error": {
        turn.status = TraceTurnStatus.ERROR;
        turn.durationMs =
          event.durationMs ?? diffMs(turn.startedAt, event.timestamp);
        turn.error = event.error ?? "Turn failed";
        break;
      }
      case "node.started": {
        const span = createSpan(
          TraceSpanKind.NODE,
          TraceSpanStatus.RUNNING,
          event,
          event.name ?? "node",
        );
        pushOpen(builder.openNodesByName, span.name, span);
        turn.spans.push(span);
        break;
      }
      case "node.completed":
      case "node.error": {
        const name = event.name ?? "node";
        const open = popOpen(builder.openNodesByName, name);
        if (open) {
          closeSpan(open, event);
        } else {
          turn.spans.push(createClosedSpan(TraceSpanKind.NODE, event, name));
        }
        break;
      }
      case "route.selected": {
        const span = createSpan(
          TraceSpanKind.ROUTE,
          TraceSpanStatus.DONE,
          event,
          event.name ?? "route",
        );
        span.decision = event.decision ?? "unknown";
        turn.spans.push(span);
        break;
      }
      case "llm.started": {
        const span = createSpan(
          TraceSpanKind.LLM,
          TraceSpanStatus.RUNNING,
          event,
          getLlmSpanName(event),
        );
        if (event.spanId) {
          builder.openLlmBySpanId.set(event.spanId, span);
        }
        pushOpen(builder.openLlmByName, span.name, span);
        turn.spans.push(span);
        break;
      }
      case "llm.completed":
      case "llm.error": {
        const name = getLlmSpanName(event);
        const bySpanId = event.spanId
          ? builder.openLlmBySpanId.get(event.spanId)
          : undefined;
        const open =
          bySpanId?.status === TraceSpanStatus.RUNNING
            ? bySpanId
            : popOpen(builder.openLlmByName, name);
        if (open) {
          if (event.spanId) {
            builder.openLlmBySpanId.delete(event.spanId);
          }
          closeSpan(open, event);
        } else {
          turn.spans.push(createClosedSpan(TraceSpanKind.LLM, event, name));
        }
        break;
      }
    }
  }

  return ordered.map(({ turn }) => turn);
}
